import { CountBadge } from "~/components/mobile/CountBadge";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Bell, ChevronLeft, House, Video, VideoOff } from "lucide-react";

import { ConnectingScreen, CryptoHost, useShellGateScreen } from "~/App";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { usePushRegistration, useSignedInLatch } from "~/hooks/usePushRegistration";
import { useRegularWidth } from "~/hooks/useFormFactor";
import { useLasting } from "~/hooks/useLasting";
import { MobileStatusContext } from "~/components/mobile/status";
import { SearchPanel } from "~/components/SearchPanel";
import { getInitials } from "~/lib/initials";
import {
  requestOpenIdToken,
  searchEvents as matrixSearchEvents,
  type SearchResultsPage,
} from "~/lib/matrix";
import { sortByActivity } from "~/lib/room-utils";
import {
  clearMobileBadge,
  setMobileAppIconBadge,
  getLaunchNotification,
  markMobileLifecycleConnected,
  onNotificationTap,
  type MobileConnectionState,
} from "~/lib/platform";
import { useToast } from "~/context/ToastContext";
import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { cachedOpenIdProvider } from "~/lib/meet-instant";
import { useMeetingActions } from "~/hooks/useMeetingActions";
import { useConversationTransition } from "~/hooks/useConversationTransition";
import { useInvitations } from "~/hooks/useInvitations";
import { useMobileConnection } from "~/hooks/useMobileConnection";
import { useAttention } from "~/hooks/useAttention";
import { MobileActivity } from "~/components/mobile/MobileActivity";
import { MobileHome } from "~/components/mobile/MobileHome";
import { MobileRoom, roomLabel } from "~/components/mobile/MobileRoom";
import { MobileThread } from "~/components/mobile/MobileThread";
import { openThreadWithTransition, ThreadLayer } from "~/components/mobile/ThreadLayer";
import { ThreadUnreadHost } from "~/components/mobile/ThreadTail";
import { MobileYou } from "~/components/mobile/MobileYou";
import { mobileStrings } from "~/components/mobile/strings";
import { useLaunchRoomLink } from "~/lib/launch-link";
import { linkedRoomId, RoomLinkContext, type OpenRoomLink } from "~/lib/permalink";
import { ICON_STROKE } from "~/lib/icon";

const MOBILE_PANEL_ID = "chat-mobile-panel";
/** Conversation list width in the tablet split layout. */
const SPLIT_LIST_WIDTH = 360;

export type MobileTab = "home" | "activity" | "you";

const TAB_ORDER: readonly MobileTab[] = ["home", "activity", "you"] as const;

export type MobileView =
  | { view: "home" }
  | { view: "room"; roomId: string }
  | { view: "thread"; roomId: string; rootId: string };

export interface MobileNavState {
  tab: MobileTab;
  /** Non-empty; `stack[0]` is always the home root. Only the home tab
   *  carries a deeper stack — Activity and You are flat. */
  stack: MobileView[];
}

export type MobileNavAction =
  | { type: "selectTab"; tab: MobileTab }
  | { type: "openRoom"; roomId: string; keepTab?: boolean }
  | { type: "openThread"; roomId: string; rootId: string; keepTab?: boolean }
  | { type: "back"; keepTab?: boolean };

export const initialMobileNav: MobileNavState = {
  tab: "home",
  stack: [{ view: "home" }],
};

/** Synthetic-history depth: one back step per home-stack level. Tabs are
 *  a lateral selector and never add depth, so the only way depth shrinks
 *  is `back` (which is always popstate-driven) — the history shim never
 *  needs to unwind entries outside popstate. */
export function mobileNavDepth(state: MobileNavState): number {
  return state.stack.length - 1;
}

/** The visible level. The stack invariant keeps it non-empty; the home
 *  root is the fallback so callers never handle `undefined`. */
export function mobileNavTop(state: MobileNavState): MobileView {
  return state.stack[state.stack.length - 1] ?? { view: "home" };
}

export function canGoBack(state: MobileNavState): boolean {
  return state.stack.length > 1;
}

export function mobileNavReducer(state: MobileNavState, action: MobileNavAction): MobileNavState {
  switch (action.type) {
    case "selectTab":
      return state.tab === action.tab ? state : { ...state, tab: action.tab };
    case "openRoom":
      return {
        tab: action.keepTab ? state.tab : "home",
        stack: [{ view: "home" }, { view: "room", roomId: action.roomId }],
      };
    case "openThread":
      return {
        tab: action.keepTab ? state.tab : "home",
        stack: [
          { view: "home" },
          { view: "room", roomId: action.roomId },
          { view: "thread", roomId: action.roomId, rootId: action.rootId },
        ],
      };
    case "back":
      // The stack belongs to the Home tab, so a pop lands on Home — back
      // from another tab surfaces the popped Home level rather than
      // unwinding a stack the user cannot see.
      return canGoBack(state)
        ? { tab: action.keepTab ? state.tab : "home", stack: state.stack.slice(0, -1) }
        : state;
  }
}

function tabDomId(tab: MobileTab): string {
  return `chat-mobile-tab-${tab}`;
}

function TabIcon({
  tab,
  youName,
  selected,
}: {
  tab: MobileTab;
  youName: string | null;
  selected: boolean;
}) {
  if (tab === "home") return <House aria-hidden size={22} strokeWidth={ICON_STROKE} />;
  if (tab === "activity") return <Bell aria-hidden size={22} strokeWidth={ICON_STROKE} />;
  const initials = youName ? getInitials(youName) : "";
  return (
    <span
      aria-hidden
      className={`flex size-6 items-center justify-center rounded-full bg-foreground/[0.12] text-[9px] font-medium ${
        selected
          ? "text-foreground/90 ring-1 ring-foreground/70 ring-offset-2 ring-offset-background"
          : "text-foreground/70"
      }`}
    >
      {initials}
    </span>
  );
}

function MobileTabBar({
  active,
  onSelect,
  youName,
  homeCount,
  activityCount,
  youNeedsAction,
  centerInset,
}: {
  active: MobileTab;
  onSelect: (tab: MobileTab) => void;
  /** The signed-in user's display name — the You tab shows their initials. */
  youName: string | null;
  /** Unread messages in rooms that are not muted. */
  homeCount: number;
  /** Mentions and unread direct messages: what needs the user. */
  activityCount: number;
  /** This device cannot read its history yet; the You tab holds the step. */
  youNeedsAction: boolean;
  /** Tablet split: centre the icons in the bar plus the home-indicator inset. */
  centerInset: boolean;
}) {
  const refs = useRef<Partial<Record<MobileTab, HTMLButtonElement | null>>>({});

  function tabCount(tab: MobileTab): number {
    return tab === "home" ? homeCount : tab === "activity" ? activityCount : 0;
  }

  function focusTab(index: number) {
    const tab = TAB_ORDER[((index % TAB_ORDER.length) + TAB_ORDER.length) % TAB_ORDER.length];
    if (!tab) return;
    onSelect(tab);
    refs.current[tab]?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusTab(index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusTab(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusTab(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusTab(TAB_ORDER.length - 1);
    }
  }

  return (
    <div
      role="tablist"
      aria-label={mobileStrings.nav.tablistLabel}
      aria-orientation="horizontal"
      className={`flex shrink-0 items-stretch border-t border-foreground/[0.04] bg-background chat-safe-left chat-safe-right ${
        centerInset
          ? "h-[calc(49px+env(safe-area-inset-bottom,0px))]"
          : "box-content h-[49px] chat-safe-bottom"
      }`}
    >
      {TAB_ORDER.map((tab, index) => {
        const selected = tab === active;
        return (
          <button
            key={tab}
            ref={(el) => {
              refs.current[tab] = el;
            }}
            type="button"
            role="tab"
            id={tabDomId(tab)}
            aria-selected={selected}
            aria-controls={MOBILE_PANEL_ID}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab)}
            onKeyDown={(event) => onKeyDown(event, index)}
            aria-label={
              tabCount(tab) > 0
                ? mobileStrings.tabBadge(mobileStrings.tabs[tab], tabCount(tab))
                : tab === "you" && youNeedsAction
                  ? mobileStrings.tabNeedsAction(mobileStrings.tabs[tab])
                  : mobileStrings.tabs[tab]
            }
            className={`relative flex flex-1 items-center justify-center outline-none transition-opacity focus-visible:shadow-neon-cyan ${
              selected ? "text-foreground/90" : "text-muted-quiet hover:text-foreground/70"
            }`}
          >
            <span className="relative flex">
              <TabIcon tab={tab} youName={youName} selected={selected} />
              <CountBadge
                count={tabCount(tab)}
                tone={tab === "activity" ? "accent" : "ink"}
                className="absolute -right-3 -top-2 ring-2 ring-background"
              />
              {tab === "you" && youNeedsAction && (
                <span
                  aria-hidden
                  data-testid="you-tab-dot"
                  className="absolute -right-1 -top-0.5 size-2 rounded-full bg-secondary ring-2 ring-background"
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MobileBackHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-1.5 px-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={mobileStrings.nav.back}
        className="-ml-1 flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none transition-opacity hover:text-foreground/90 focus-visible:shadow-neon-cyan"
      >
        <ChevronLeft size={18} strokeWidth={ICON_STROKE} />
      </button>
      {title.length > 0 && (
        <span className="truncate text-sm font-light text-foreground/90">{title}</span>
      )}
    </div>
  );
}

export function RoomMeetingControls({
  active,
  canControl,
  isHost,
  busy,
  joinBusy,
  onStart,
  onEnd,
  onJoin,
}: {
  active: boolean;
  canControl: boolean;
  isHost: boolean;
  busy: boolean;
  joinBusy: boolean;
  onStart: () => void;
  onEnd: () => void;
  onJoin: () => void;
}): ReactNode {
  if (active && canControl && isHost) {
    return (
      <>
        <button
          type="button"
          aria-label="Join meeting"
          onClick={() => void onJoin()}
          disabled={joinBusy}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-foreground/25 bg-transparent px-3 py-1.5 text-xs font-medium text-foreground outline-none focus-visible:shadow-neon-cyan"
        >
          <Video aria-hidden size={12} strokeWidth={ICON_STROKE} />
          Join
        </button>
        <button
          type="button"
          aria-label="End meeting"
          onClick={() => void onEnd()}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border-none px-3 py-2 text-sm font-medium text-foreground outline-none transition-colors hover:bg-foreground/[0.08] focus-visible:shadow-neon-cyan"
        >
          <VideoOff aria-hidden size={18} strokeWidth={ICON_STROKE} />
          End meeting
        </button>
      </>
    );
  }
  if (canControl && !active) {
    return (
      <button
        type="button"
        aria-label="Start meeting"
        onClick={() => void onStart()}
        disabled={busy}
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border-none bg-transparent text-foreground outline-none transition-colors hover:bg-foreground/[0.08] focus-visible:shadow-neon-cyan"
      >
        <Video aria-hidden size={18} strokeWidth={ICON_STROKE} />
      </button>
    );
  }
  if (active) {
    return (
      <button
        type="button"
        aria-label="Join meeting"
        onClick={() => void onJoin()}
        disabled={joinBusy}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-foreground/25 bg-transparent px-3 py-1.5 text-xs font-medium text-foreground outline-none focus-visible:shadow-neon-cyan"
      >
        <Video aria-hidden size={12} strokeWidth={ICON_STROKE} />
        Join
      </button>
    );
  }
  return null;
}

/**
 * Presentational shell chrome: an optional back header, the labelled
 * tab panel, and the tab bar. Holds no Matrix/crypto state so it renders
 * — and is axe-checked — without the provider stack. `levelKey` drives
 * the slide animation (a fresh mount per level); `focusKey` is the
 * stack-top identity and moves focus on a stack push/pop only — not on a
 * tab switch, which would fight the tab bar's roving-tabindex focus.
 */
export function MobileShellChrome({
  nav,
  levelKey,
  focusKey,
  headerTitle,
  connection = "online",
  onSelectTab,
  onBack,
  showTabBar = true,
  youName = null,
  homeCount = 0,
  activityCount = 0,
  youNeedsAction = false,
  split = false,
  home,
  showHome = false,
  children,
}: {
  nav: MobileNavState;
  levelKey: string;
  focusKey: string;
  headerTitle: string | null;
  connection?: MobileConnectionState;
  onSelectTab: (tab: MobileTab) => void;
  onBack: () => void;
  /** Phones hide the tab bar inside a conversation (the room owns the
   *  screen and its composer sits on the bottom edge). */
  showTabBar?: boolean;
  youName?: string | null;
  homeCount?: number;
  activityCount?: number;
  youNeedsAction?: boolean;
  split?: boolean;
  home?: ReactNode;
  showHome?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const firstRenderRef = useRef(true);
  // Every return to the foreground passes through "connecting"; only a
  // connection that keeps us waiting is worth a line. Offline is said at once.
  const connectingLasts = useLasting(connection === "connecting");
  const connectionLabel =
    connection === "offline"
      ? mobileStrings.connection.offline
      : connectingLasts
        ? mobileStrings.connection.connecting
        : null;

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    panelRef.current?.focus();
  }, [focusKey]);

  return (
    <MobileStatusContext.Provider value={connectionLabel}>
      <div className="flex h-full flex-col bg-background text-foreground">
        <span role="status" aria-live="polite" className="sr-only">
          {connectionLabel}
        </span>
        <div
          ref={panelRef}
          role="tabpanel"
          id={MOBILE_PANEL_ID}
          aria-labelledby={tabDomId(nav.tab)}
          tabIndex={-1}
          className="flex min-h-0 flex-1 flex-col outline-none chat-safe-top chat-safe-left chat-safe-right"
        >
          {headerTitle !== null && <MobileBackHeader title={headerTitle} onBack={onBack} />}
          {home && (
            <div
              hidden={!showHome}
              className="min-h-0 flex-1"
              style={{ display: showHome ? "flex" : "none", flexDirection: "column" }}
            >
              {home}
            </div>
          )}
          <div
            key={levelKey}
            hidden={showHome}
            data-tab-root={(TAB_ORDER as readonly string[]).includes(levelKey) || undefined}
            className="chat-mobile-level min-h-0 flex-1 flex-col"
            style={
              {
                display: showHome ? "none" : "flex",
                // Without the tab bar the level reaches the screen's bottom
                // edge; its composer stays above the home indicator, close
                // to it the way the system's own message field is.
                "--chat-phone-bottom-inset": showTabBar
                  ? "0px"
                  : "max(0px, calc(env(safe-area-inset-bottom, 0px) - 16px))",
              } as CSSProperties
            }
          >
            {children}
          </div>
        </div>
        {showTabBar && (
          <MobileTabBar
            active={nav.tab}
            onSelect={onSelectTab}
            youName={youName}
            homeCount={homeCount}
            activityCount={activityCount}
            youNeedsAction={youNeedsAction}
            centerInset={split}
          />
        )}
      </div>
    </MobileStatusContext.Provider>
  );
}

type AttentionChromeProps = Omit<
  Parameters<typeof MobileShellChrome>[0],
  "connection" | "homeCount" | "activityCount"
>;

function AttentionChrome({ children, ...rest }: AttentionChromeProps) {
  const { state } = useMatrix();
  const rooms = useMatrixRooms();
  const { homeCount, activityCount, appBadgeCount } = useAttention();
  const reachability = useMobileConnection(appBadgeCount);
  const connection =
    reachability === "online" && (state === "syncing" || state === "reconnecting")
      ? "connecting"
      : reachability;
  useEffect(() => {
    if (state !== "synced" && state !== "syncing") return;
    if (state === "syncing" && rooms.length === 0) return;
    void setMobileAppIconBadge(appBadgeCount);
  }, [state, rooms.length, appBadgeCount]);
  return (
    <MobileShellChrome
      {...rest}
      homeCount={homeCount}
      activityCount={activityCount}
      connection={connection}
    >
      {children}
    </MobileShellChrome>
  );
}

function MobilePanePlaceholder({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4">
      <span className="text-[10px] uppercase tracking-widest text-muted-quiet">{heading}</span>
      <p className="text-sm font-light text-muted-foreground">{body}</p>
    </div>
  );
}

export function MobileShell() {
  const {
    state,
    setActiveRoomId,
    client,
    openThread,
    activeThreadRoot,
    signOut,
    registerSignOutTeardown,
    userId,
    jumpToEvent,
    displayName,
    canUnlockHistory,
  } = useMatrix();
  const rooms = useMatrixRooms();
  const { push: pushToast } = useToast();
  const { meetOrigins } = useEmbedRuntime();
  const signedIn = useSignedInLatch(state, userId);
  const [searchOpen, setSearchOpen] = useState(false);
  // Interim room list for the room/thread lookups: every joined,
  // non-archived room (DMs and unparented channels included). The Home
  // tab owns its own scoping inside `MobileHome`.
  const mobileRooms = useMemo(
    () => sortByActivity(rooms.filter((room) => !room.isSpace && room.archived === null)),
    [rooms],
  );
  const [nav, dispatch] = useReducer(mobileNavReducer, initialMobileNav);
  const { invites: invitedRooms } = useInvitations(client);
  const depthRef = useRef(0);
  const { open: openConversation, close: closeConversation } = useConversationTransition();

  // Tablet widths open the shell into two panes: the tab content stays on
  // the left while the conversation fills the right (Slack/Mail on iPad).
  const split = useRegularWidth();
  const top = mobileNavTop(nav);
  // A thread belongs to its room: the room stays active under it, or the
  // context's room-change rule would close the thread it just opened.
  const activeRoomId =
    (split || nav.tab === "home") && (top.view === "room" || top.view === "thread")
      ? top.roomId
      : null;

  const activeRoom = mobileRooms.find((room) => room.id === activeRoomId) ?? null;
  const openIdProvider = useMemo(
    () =>
      cachedOpenIdProvider(async () => {
        if (!client) throw new Error("no matrix client");
        return requestOpenIdToken(client);
      }),
    [client],
  );
  const meeting = useMeetingActions({
    client,
    room: activeRoom,
    userId,
    openIdProvider,
    pushToast,
    meetOrigins,
    rooms,
  });
  const roomHeaderExtra =
    nav.tab === "home" &&
    top.view === "room" &&
    (meeting.canControlMeeting || meeting.activeMeeting) ? (
      <RoomMeetingControls
        active={!!meeting.activeMeeting}
        canControl={meeting.canControlMeeting}
        isHost={meeting.isHost}
        busy={meeting.busy}
        joinBusy={meeting.joinBusy}
        onStart={() => void meeting.start()}
        onEnd={() => void meeting.end()}
        onJoin={() => void meeting.join()}
      />
    ) : null;

  // Align the shared context room pointer with the visible room before
  // paint so context consumers (read receipts, typing) track the room
  // the user is actually viewing. Clears to null off a room.
  useLayoutEffect(() => {
    setActiveRoomId(activeRoomId);
  }, [activeRoomId, setActiveRoomId]);

  useEffect(() => {
    if (activeRoomId) void clearMobileBadge();
  }, [activeRoomId]);

  const openNotificationRoom = useCallback(
    (roomId: string) => {
      if (invitedRooms.some((invite) => invite.roomId === roomId)) {
        dispatch({ type: "selectTab", tab: "home" });
        void clearMobileBadge();
        return;
      }
      dispatch({ type: "openRoom", roomId, keepTab: split });
      void clearMobileBadge();
    },
    [invitedRooms, split],
  );

  const runSearch = useCallback(
    async (query: string, roomId?: string, nextBatch?: string): Promise<SearchResultsPage> => {
      if (!client) return { results: [], nextBatch: null };
      return matrixSearchEvents(client, query, roomId, nextBatch);
    },
    [client],
  );

  const jumpFromSearch = useCallback(
    (roomId: string, eventId: string) => {
      setSearchOpen(false);
      dispatch({ type: "openRoom", roomId, keepTab: split });
      jumpToEvent({ roomId, eventId });
    },
    [jumpToEvent, split],
  );

  const openRoom = useCallback(
    (roomId: string) => {
      const open = () => dispatch({ type: "openRoom", roomId, keepTab: split });
      if (split) open();
      else openConversation(roomId, open);
    },
    [openConversation, split],
  );

  const openRoomLink = useCallback<OpenRoomLink>(
    (link) => {
      const roomId = linkedRoomId(mobileRooms, link);
      if (!roomId) return false;
      openRoom(roomId);
      if (link.eventId) jumpToEvent({ roomId, eventId: link.eventId });
      return true;
    },
    [mobileRooms, openRoom, jumpToEvent],
  );

  usePushRegistration({
    client,
    signedIn,
    onNotificationTap: openNotificationRoom,
    registerSignOutTeardown,
  });

  useEffect(() => {
    if (state === "synced") void markMobileLifecycleConnected();
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    let unlistenTap: (() => void) | null = null;
    void getLaunchNotification().then((notification) => {
      if (!cancelled && notification) openNotificationRoom(notification.roomId);
    });
    void onNotificationTap((notification) => {
      openNotificationRoom(notification.roomId);
    }).then((unlisten) => {
      if (cancelled) unlisten();
      else unlistenTap = unlisten;
    });
    return () => {
      cancelled = true;
      unlistenTap?.();
    };
  }, [openNotificationRoom]);

  // History shim: each deeper level adds one synthetic history entry.
  // `popstate` only pops when the target entry is shallower than the
  // current depth, so a forward navigation is not mistaken for a back.
  useEffect(() => {
    const depth = mobileNavDepth(nav);
    for (let d = depthRef.current; d < depth; d++) {
      window.history.pushState({ chatMobileNavDepth: d + 1 }, "");
    }
    depthRef.current = depth;
  }, [nav]);

  useEffect(() => {
    function onPopState(event: PopStateEvent) {
      const navState = event.state as { chatMobileNavDepth?: number } | null;
      const targetDepth =
        typeof navState?.chatMobileNavDepth === "number" ? navState.chatMobileNavDepth : 0;
      if (targetDepth < depthRef.current) {
        const back = () => dispatch({ type: "back" });
        if (!split && top.view === "room") closeConversation(top.roomId, back);
        else back();
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [split, top, closeConversation]);

  const gateScreen = useShellGateScreen({
    connectingLabel: mobileStrings.shell.connecting,
    syncingLabel: mobileStrings.shell.syncing,
  });
  useLaunchRoomLink(openRoomLink, gateScreen === null && state === "synced");
  if (gateScreen) return gateScreen;

  const goBack = split
    ? () => dispatch({ type: "back", keepTab: true })
    : () => window.history.back();

  function renderRoomLevel(
    view: Extract<MobileView, { view: "room" }>,
    headerActions?: ReactNode,
  ): ReactNode {
    const activeRoom = mobileRooms.find((room) => room.id === view.roomId) ?? null;
    return client && activeRoom ? (
      <MobileRoom
        client={client}
        room={activeRoom}
        onBack={goBack}
        showBack={!split}
        headerActions={headerActions}
        onOpenThread={(root) => {
          const open = () => {
            openThread({ roomId: view.roomId, root });
            dispatch({ type: "openThread", roomId: view.roomId, rootId: root.id, keepTab: split });
          };
          if (split) open();
          else openThreadWithTransition(root.id, open);
        }}
      />
    ) : (
      <ConnectingScreen label={mobileStrings.shell.syncing} />
    );
  }

  function renderThreadLevel(view: Extract<MobileView, { view: "thread" }>): ReactNode {
    return client &&
      activeThreadRoot &&
      activeThreadRoot.roomId === view.roomId &&
      activeThreadRoot.root.id === view.rootId ? (
      <MobileThread roomId={view.roomId} root={activeThreadRoot.root} onClose={goBack} />
    ) : (
      <MobilePanePlaceholder
        heading={mobileStrings.thread.title}
        body={mobileStrings.thread.empty}
      />
    );
  }

  function renderTabRoot(): ReactNode {
    if (nav.tab === "activity") return <MobileActivity onOpenRoom={openRoom} />;
    if (nav.tab === "you") return <MobileYou onSignOut={() => void signOut()} />;
    return (
      <MobileHome
        onOpenRoom={openRoom}
        activeRoomId={activeRoomId}
        onOpenSearch={() => setSearchOpen(true)}
      />
    );
  }

  if (split) {
    const pane =
      top.view === "room" ? (
        renderRoomLevel(top)
      ) : top.view === "thread" ? (
        renderThreadLevel(top)
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm font-light text-muted-foreground">{mobileStrings.split.empty}</p>
        </div>
      );
    const paneKey =
      top.view === "room"
        ? `room:${top.roomId}`
        : top.view === "thread"
          ? `thread:${top.roomId}:${top.rootId}`
          : "empty";
    return (
      <ThreadUnreadHost>
        <CryptoHost>
          <div
            data-chat-mobile-split
            className="grid h-full bg-background text-foreground"
            style={{ gridTemplateColumns: `${SPLIT_LIST_WIDTH}px minmax(0, 1fr)` }}
          >
            <div className="flex min-h-0 flex-col border-r border-foreground/[0.04]">
              <AttentionChrome
                nav={nav}
                levelKey={nav.tab}
                focusKey={nav.tab}
                headerTitle={null}
                onSelectTab={(tab) => dispatch({ type: "selectTab", tab })}
                onBack={goBack}
                youName={displayName}
                youNeedsAction={canUnlockHistory}
                split
              >
                {renderTabRoot()}
              </AttentionChrome>
            </div>
            <main
              key={paneKey}
              aria-label={mobileStrings.split.paneLabel}
              className="chat-mobile-level flex min-h-0 flex-col pb-[calc(env(safe-area-inset-bottom,0px)/2)] chat-safe-top chat-safe-right"
            >
              {pane}
            </main>
          </div>
          <SearchPanel
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            search={runSearch}
            rooms={mobileRooms}
            onJump={jumpFromSearch}
          />
        </CryptoHost>
      </ThreadUnreadHost>
    );
  }

  let content: ReactNode;
  // Room and Thread render their own headers (member count, `⋯`, root
  // preview); the other levels are headerless, so the chrome's optional
  // back-header is unused here.
  const headerTitle: string | null = null;
  let levelKey: string;
  let threadLayer: ReactNode = null;

  if (nav.tab === "activity" || nav.tab === "you") {
    content = renderTabRoot();
    levelKey = nav.tab;
  } else if (top.view === "room") {
    levelKey = `room:${top.roomId}`;
    content = renderRoomLevel(top, roomHeaderExtra);
  } else if (top.view === "thread") {
    // The room stays mounted under the thread card, so its key and scroll never change.
    levelKey = `room:${top.roomId}`;
    content = renderRoomLevel({ view: "room", roomId: top.roomId }, roomHeaderExtra);
    const threadRoom = mobileRooms.find((room) => room.id === top.roomId) ?? null;
    if (
      threadRoom &&
      activeThreadRoot &&
      activeThreadRoot.roomId === top.roomId &&
      activeThreadRoot.root.id === top.rootId
    ) {
      threadLayer = (
        <ThreadLayer
          key={`thread:${top.roomId}:${top.rootId}`}
          roomId={top.roomId}
          root={activeThreadRoot.root}
          roomName={roomLabel(threadRoom)}
          onClose={() => window.history.back()}
        />
      );
    }
  } else {
    levelKey = "home";
    content = nav.tab === "home" ? null : renderTabRoot();
  }

  // Focus moves on a stack push/pop (the visible level), not on a tab
  // switch — so the tab bar keeps focus during arrow-key navigation.
  // The thread card takes focus itself; the room beneath keeps its level focus key.
  const focusKey = top.view === "room" || top.view === "thread" ? `room:${top.roomId}` : "home";

  const shell = (
    <CryptoHost>
      <AttentionChrome
        nav={nav}
        levelKey={levelKey}
        focusKey={focusKey}
        headerTitle={headerTitle}
        onSelectTab={(tab) => dispatch({ type: "selectTab", tab })}
        onBack={() => window.history.back()}
        showTabBar={nav.tab !== "home" || top.view === "home"}
        youName={displayName}
        youNeedsAction={canUnlockHistory}
        home={nav.tab === "home" ? renderTabRoot() : undefined}
        showHome={nav.tab === "home" && top.view === "home"}
      >
        {content}
      </AttentionChrome>
      {threadLayer}
      <SearchPanel
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        search={runSearch}
        rooms={mobileRooms}
        onJump={jumpFromSearch}
      />
    </CryptoHost>
  );
  return (
    <ThreadUnreadHost>
      <RoomLinkContext.Provider value={openRoomLink}>{shell}</RoomLinkContext.Provider>
    </ThreadUnreadHost>
  );
}
